import { uniq } from 'lodash';
import { useMemo } from 'react';

import { flattenSteps } from 'dashboard/lib/workflows';
import { WorkflowError, WorkflowErrorType } from 'dashboard/models/workflow';
import { WorkflowRunStatus } from 'dashboard/models/workflow-run';
import {
  isStepOverrideRunVerificationType,
  WorkflowRunSimulateContext,
  WorkflowRunSimulatedAttributes,
} from 'dashboard/models/workflow-run-simulated';
import { WorkflowRunStep } from 'dashboard/models/workflow-step';
import {
  AllWorkflowAction,
  WorkflowStep,
  WorkflowStepAction,
  WorkflowStepType,
} from 'dashboard/models/workflow-version';
import { getWorkflowRunStepErrors } from 'dashboard/views/DashboardWorkflowRunsShowView/utils';
import {
  SUPPORTED_RESOLVED_INPUT_TYPES,
  TRIGGER_DEFINITION_PANEL_SUPPORTED_SCHEMA_TYPES,
} from 'dashboard/views/DashboardWorkflowRunsSimulateShowView/constants';
import { FieldSchema, FieldSchemaType } from 'lib/fields';

const useSimulatedWorkflowRunValidation = (
  simulateContext: WorkflowRunSimulateContext,
  triggerFieldSchemas: FieldSchema[],
  triggerId: string,
  workflowRunSteps: WorkflowRunStep[],
  stepsConfig: WorkflowStep[],
  simulatedRunAttributes: WorkflowRunSimulatedAttributes,
) => {
  const errorsTrigger: WorkflowError[] = useMemo(() => {
    const configTimeError = simulateContext['trigger']
      ? []
      : [
          {
            type: WorkflowErrorType.Save,
            token: triggerId,
            message: 'Trigger override must be configured',
          },
        ];
    const runTimeError =
      simulatedRunAttributes.status === WorkflowRunStatus.Failed &&
      simulatedRunAttributes['failure-reason']
        ? [
            {
              type: WorkflowErrorType.Run,
              token: triggerId,
              message: simulatedRunAttributes['failure-reason'],
            },
          ]
        : [];
    return [...configTimeError, ...runTimeError];
  }, [simulateContext, triggerId, simulatedRunAttributes]);

  const errorsRunVerificationStep: WorkflowError[] = useMemo(() => {
    return Object.keys(simulateContext.overrides?.steps ?? {}).reduce<WorkflowError[]>(
      (errors, key) => {
        const step = simulateContext.overrides?.steps?.[key];
        return [
          ...errors,
          ...(step && isStepOverrideRunVerificationType(step) && step['check-stubs']?.length === 0
            ? [
                {
                  type: WorkflowErrorType.Save,
                  token: key,
                  message: 'At least one check result must be configured',
                },
              ]
            : []),
        ];
      },
      [],
    );
  }, [simulateContext.overrides?.steps]);

  const requiredFields: string[] = useMemo(() => {
    return triggerFieldSchemas
      .filter((schema) => schema.config.required && !schema.config['deactivated-at'])
      .map((schema) => schema.key);
  }, [triggerFieldSchemas]);

  const referencedFields: string[] = useMemo(() => {
    const allReferencedTriggerFields: string[] = flattenSteps(stepsConfig)
      .filter(
        (step): step is WorkflowStepAction & { action: AllWorkflowAction } =>
          step.type === WorkflowStepType.Action && step.action != null,
      )
      .flatMap((step) => {
        return Object.entries(step.action.params).flatMap(([key, stepToTriggerMapping]) => {
          if (SUPPORTED_RESOLVED_INPUT_TYPES.includes(key)) {
            return Object.values(stepToTriggerMapping);
          }
          return [];
        });
      });

    const uniqueReferencedTriggerFields = allReferencedTriggerFields
      .filter((field) => field.startsWith('trigger.fields.'))
      .map((field) => field.replace('trigger.fields.', ''));
    return uniq(uniqueReferencedTriggerFields);
  }, [stepsConfig]);

  const errorsRequiredFields: WorkflowError[] = useMemo(() => {
    const shouldError = (field: string) => {
      const fieldOverride = simulateContext.trigger?.overrides?.[`fields.${field}`];
      const schemaType = triggerFieldSchemas.find((schema) => schema.key === field)?.type;

      if (
        !schemaType ||
        schemaType === FieldSchemaType.Boolean ||
        !TRIGGER_DEFINITION_PANEL_SUPPORTED_SCHEMA_TYPES.includes(schemaType)
      ) {
        return false;
      }

      return fieldOverride == null || fieldOverride === '';
    };

    return requiredFields.reduce<WorkflowError[]>((errors, field) => {
      return [
        ...errors,
        ...(shouldError(field)
          ? [
              {
                type: WorkflowErrorType.Save,
                token: triggerId,
                field: field,
                message: `${
                  triggerFieldSchemas.find((schema) => schema.key === field)?.label || field
                } must be populated on trigger`,
              },
            ]
          : []),
      ];
    }, []);
  }, [requiredFields, simulateContext.trigger?.overrides, triggerFieldSchemas, triggerId]);

  return useMemo(() => {
    const validationErrors = [
      ...getWorkflowRunStepErrors(workflowRunSteps),
      ...errorsTrigger,
      ...errorsRunVerificationStep,
      ...errorsRequiredFields,
    ];
    return { validationErrors, requiredFields, referencedFields };
  }, [
    workflowRunSteps,
    errorsTrigger,
    errorsRunVerificationStep,
    errorsRequiredFields,
    requiredFields,
    referencedFields,
  ]);
};

export default useSimulatedWorkflowRunValidation;
